// Sudoku Flags - Phase 1 (no auth/DB) per-flag stats, per docs/SUDOKU_FLAGS_LEADERBOARD.md.
// An append-only event log in localStorage, pruned to the last 35 days
// (a few days of slack past the longest window we compute, 30 days).
// This is explicitly per-device/per-browser "my stats", not a real
// cross-player leaderboard - that requires Phase 2's server + auth.

export type FlagEvent = { id: string; flagSlug: string; points: number; ts: number }

const STORAGE_KEY = 'sudokuflags_flag_events'
const RETENTION_MS = 35 * 24 * 60 * 60 * 1000

export const WINDOW_24H = 24 * 60 * 60 * 1000
export const WINDOW_7D = 7 * 24 * 60 * 60 * 1000
export const WINDOW_30D = 30 * 24 * 60 * 60 * 1000

function readEvents(): FlagEvent[] {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function writeEvents(events: FlagEvent[]): void {
    try {
        const cutoff = Date.now() - RETENTION_MS
        const pruned = events.filter((e) => e.ts >= cutoff)
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned))
    } catch {
        // localStorage unavailable (private mode, quota, etc.) - stats just won't persist
    }
}

function makeId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

// Appends one event per entry, returns the created events (with their ids)
// so the caller can later reverse them via removeFlagEvents (e.g. on undo).
export function recordFlagEvents(entries: Array<{ flagSlug: string; points: number }>): FlagEvent[] {
    if (typeof window === 'undefined' || entries.length === 0) return []
    const now = Date.now()
    const created = entries.map((e) => ({ id: makeId(), flagSlug: e.flagSlug, points: e.points, ts: now }))
    const events = readEvents()
    writeEvents([...events, ...created])
    return created
}

export function removeFlagEvents(ids: string[]): void {
    if (typeof window === 'undefined' || ids.length === 0) return
    const idSet = new Set(ids)
    const events = readEvents()
    writeEvents(events.filter((e) => !idSet.has(e.id)))
}

// Totals per flag within the last `windowMs` (omit for all-time), sorted
// highest points first. Flags with a net-zero or negative total are still
// included as long as they have at least one event in the window.
export function getFlagTotals(windowMs?: number): Array<{ flagSlug: string; points: number; games: number }> {
    if (typeof window === 'undefined') return []
    const cutoff = windowMs ? Date.now() - windowMs : 0
    const events = readEvents().filter((e) => e.ts >= cutoff)
    const totals = new Map<string, { points: number; games: number }>()
    for (const e of events) {
        const current = totals.get(e.flagSlug) || { points: 0, games: 0 }
        current.points += e.points
        current.games += 1
        totals.set(e.flagSlug, current)
    }
    return Array.from(totals.entries())
        .map(([flagSlug, v]) => ({ flagSlug, points: v.points, games: v.games }))
        .sort((a, b) => b.points - a.points)
}
